import type { ELightGalleryType } from '@quesmed/types/models';
import { useParams } from 'react-router-dom';
import type { IAssetSchema } from '@/config';
import {
  CreateAssetsDocument,
  CreateJobAssetsDocument,
  GetJobAssetsDocument,
  RemoveAssetsDocument,
  RemoveJobAssetsDocument,
} from '@/graphql/gql/graphql';
import { useMutation } from '@/libs';
import { useRecord } from '@/store';
import { useAsset } from '@/store/asset';

export const useAssetActions = () => {
  const currentRecord = useRecord.use.currentRecord();
  const { id: jobId } = useParams<{ id: string }>();
  const [removeJobAssets, { loading: removeJobAssetsLoading }] = useMutation(RemoveJobAssetsDocument, {
    refetchQueries: [GetJobAssetsDocument],
  });
  const [createJobAssets, { loading: createJobAssetsLoading }] = useMutation(CreateJobAssetsDocument, {
    refetchQueries: [GetJobAssetsDocument],
  });
  const [removeAssets, { loading: removeAssetsLoading }] = useMutation(RemoveAssetsDocument);
  const [createAssets, { loading: createAssetsLoading }] = useMutation(CreateAssetsDocument);

  const setAssetMapping = useAsset.use.setAssetMapping();

  const removeUnlinkedJobAssets = async (assetIds: string[]) => {
    if (!assetIds.length || !jobId) return null;

    const { data } = await removeJobAssets({
      variables: {
        ids: assetIds,
        unlink: true,
        // jobId,
        // recordId: currentRecord?.id ?? null,
      },
    });

    if (data?.admin?.removeJobAssets) {
      return data.admin.removeJobAssets;
    }
    return null;
  };

  const resetJobAssets = async ({
    resetForm,
    resetMapping,
    oldAssets,
    assetIds,
  }: {
    oldAssets?: IAssetSchema[];
    resetMapping?: boolean;
    resetForm?: () => void;
    assetIds: string[];
  }) => {
    // Remove any unlinked assets before resetting
    const removedAssets = await removeUnlinkedJobAssets(assetIds);
    if (resetForm) {
      resetForm();
    }

    if (resetMapping) {
      // Always set asset mapping to the latest assets in the form
      if (removedAssets) {
        setAssetMapping(currentRecord?.assets ?? []);
      } else {
        setAssetMapping((oldAssets || []) as IAssetSchema[]);
      }
    }
  };

  const createJobAsset = async (
    fileData: Array<{
      file: File;
      name: string;
      sensitive: boolean;
      caption?: string | null | undefined;
    }>,
    type: ELightGalleryType,
    index: number,
    choiceLabel?: string | null,
    // referenceId?: string | null,
  ) => {
    if (!jobId) {
      throw new Error('Job ID is required');
    }
    return createJobAssets({
      variables: {
        // jobId,
        // // purpose is to get back assets linked to the record if present
        // recordId: currentRecord?.id ?? null,
        // // purpose is to get back assets linked to the referenced record if present
        // referenceId: referenceId ?? null,
        inputs: fileData.map((f) => ({
          jobId,
          type,
          choiceLabel: choiceLabel ?? null,
          file: f.file,
          // pass null as we don't want to link the asset to the record yet
          recordId: null,
          caption: f.caption,
          name: f.name,
          sensitive: f.sensitive,
          index,
        })),
      },
    });
  };

  const removeJobAsset = async (asset: IAssetSchema) => {
    return removeJobAssets({
      variables: {
        ids: [asset.id],
        unlink: true,
        // jobId: asset.jobId,
        // // purpose is to get back assets linked to the record if present
        // recordId: currentRecord?.id ?? null,
        // // purpose is to get back assets linked to the referenced record if present
        // referenceId: referenceId ?? null,
      },
    });
  };

  const removeUnlinkedAssets = async (assetIds: string[]) => {
    if (!assetIds.length) return null;

    const { data } = await removeAssets({
      variables: {
        ids: assetIds,
      },
    });

    if (data?.admin?.removeAssets) {
      return data.admin.removeAssets;
    }
    return null;
  };

  const resetAssets = async ({
    resetForm,
    resetMapping,
    oldAssets,
    assetIds,
  }: {
    oldAssets?: IAssetSchema[];
    resetMapping?: boolean;
    resetForm?: () => void;
    assetIds: string[];
  }) => {
    // Remove any unlinked assets before resetting
    await removeUnlinkedAssets(assetIds);
    if (resetForm) {
      resetForm();
    }

    if (resetMapping) {
      setAssetMapping(oldAssets || []);
    }
  };

  const createAsset = async (
    fileData: Array<{
      file: File;
      name: string;
      sensitive: boolean;
      caption?: string | null | undefined;
    }>,
    // type: ELightGalleryType,
    // index: number,
    // choiceLabel?: string | null,
    // referenceId?: string | null,
  ) => {
    return createAssets({
      variables: {
        inputs: fileData.map((f) => ({
          // choiceLabel: choiceLabel ?? null,
          file: f.file,
          // pass null as we don't want to link the asset to the record yet
          caption: f.caption,
          name: f.name,
          sensitive: f.sensitive,
          // index,
        })),
      },
    });
  };

  const removeAsset = async (asset: IAssetSchema) => {
    return removeAssets({
      variables: {
        ids: [asset.picture?.id as string],
      },
    });
  };

  return {
    removeUnlinkedJobAssets,
    removeAssetsLoading: removeJobAssetsLoading || removeAssetsLoading,
    createAssetsLoading: createJobAssetsLoading || createAssetsLoading,
    resetJobAssets,
    removeJobAsset,
    createJobAsset,
    resetAssets,
    removeAsset,
    createAsset,
  };
};
